import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

// Local imports
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import LoyaltyProgramForm from "../../components/Loyalty/LoyaltyProgramForm";
import { LoyaltyProgramTemplateFormData } from "../../models/loyalty-program-template";
import { MoreRewardsConfig } from "../../database/more-rewards";
import { DbWrite } from "../../database";
import { useAuthStore } from "../../store/firebase-auth";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { logger } from "../../logging";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import * as urls from "../../urls";

interface Props {
  siteKey: string;
}

export default function CreateLoyaltyProgramContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [moreRewardsConfig, setMoreRewardsConfig] =
    useState<MoreRewardsConfig | null>(null);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);
  const siteKeyLocations = useSiteKeyLocationsStore(
    (state) => state.siteKeyLocationList,
  );
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  // Set MoreRewards config from store
  useEffect(() => {
    if (siteKeyDoc?.customizations?.moreRewards) {
      setMoreRewardsConfig(siteKeyDoc.customizations.moreRewards);
    } else {
      setMoreRewardsConfig(null);
    }
  }, [siteKeyDoc]);

  const handleSubmit = async (formData: LoyaltyProgramTemplateFormData) => {
    if (!firebaseUser) {
      setSubmitError("User authentication required");
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      // Map form data to API structure
      const loyaltyProgram = {
        siteKey,
        name: formData.name,
        description: formData.description,
        isActive: formData.isActive,
        programType: formData.programType as
          | "pointsBased"
          | "discountBased"
          | "cashback"
          | "referral"
          | "recurringDiscount",
        triggerEvent: formData.triggerEvent as
          | "invoiceCreated"
          | "invoicePaid"
          | "customerReferral"
          | "businessReferral",
        rewardType: formData.rewardType as
          | "points"
          | "percentageDiscount"
          | "fixedDiscount"
          | "commission"
          | "freeService",
        rewardValue: formData.rewardValue,
        maxRewardPerJob: formData.maxRewardPerJob,
        maxRewardPerInvoice: formData.maxRewardPerInvoice,
        maxRewardPerCustomer: formData.maxRewardPerCustomer,
        validityPeriod: formData.validityPeriod,
        minimumSpend: formData.minimumSpend,
        integrationConfig: formData.integrationConfig?.moreRewards?.enabled
          ? {
              moreRewards: {
                enabled: formData.integrationConfig.moreRewards.enabled,
                offerCode: formData.integrationConfig.moreRewards.offerCode,
              },
            }
          : undefined,
      };

      // Create in database
      await DbWrite.loyaltyPrograms.create(loyaltyProgram);

      addToastMessage({
        id: createToastMessageID(),
        message: "Loyalty program created successfully!",
        type: "success",
        dialog: false,
      });
      logger.info("Loyalty program created successfully", {
        siteKey,
        programName: formData.name,
      });

      // Navigate back to programs list
      navigate(urls.LOYALTY_PROGRAMS_URL);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create loyalty program";
      setSubmitError(errorMessage);
      logger.error("Error creating loyalty program:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: errorMessage,
        type: "error",
        dialog: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(urls.LOYALTY_PROGRAMS_URL);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <BaseButtonSecondary onClick={handleCancel}>
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Programs
        </BaseButtonSecondary>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Create Loyalty Program
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Set up a new loyalty program to reward your customers
          </p>
        </div>
      </div>

      {/* Form */}
      <LoyaltyProgramForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
        submitError={submitError}
        moreRewardsConfig={moreRewardsConfig}
        siteKeyID={siteKey}
        siteKeyLocations={siteKeyLocations}
      />
    </div>
  );
}
